import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { Subject, BehaviorSubject, Observable } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import { MobileViewService } from '../../../services/mobile-view.service';
import { UntilDestroy } from '@ngneat/until-destroy';
import { IProductResponse } from '../../interfaces';
import { NgSelectComponent } from '@ng-select/ng-select';
import { FormControl } from '@angular/forms';

const CustomMessages = {
  NotFoundMessage: $localize`No products were found. Please continue by entering the details below.`,
  TypeToSearchMessage: $localize`Type to search`,
};

@UntilDestroy()
@Component({
  selector: 'portal-product-catalog-search-box',
  templateUrl: './product-catalog-search-box.component.html',
  standalone: false,
})
export class ProductCatalogSearchBoxComponent implements OnInit, OnChanges {
  @Input() products: IProductResponse[] = [];
  @Input() loading = false;
  @Input() showLoadMore = false;
  @Input() productControl: FormControl;
  @Output() loadMore: EventEmitter<void> = new EventEmitter();
  @Output() searchTerm: EventEmitter<string> = new EventEmitter();
  @Output() selectedProduct: EventEmitter<IProductResponse> = new EventEmitter();
  @Output() resetProduct: EventEmitter<void> = new EventEmitter();
  @ViewChild('productSelect') productSelect: NgSelectComponent;

  isDeviceMobile$: Observable<boolean>;
  isAddToCatalogOpen = false;

  updatedProducts: IProductResponse[] = [];
  selectedProductId: string;

  notFoundMessage = CustomMessages.TypeToSearchMessage;

  readonly extraOptions: IProductResponse[] = [
    {
      id: 'add-to-catalog',
      name: '',
      createdAt: '',
      updatedAt: '',
      entityId: '',
      price: null,
      currency: '',
      sku: '',
    },
    {
      id: 'manual-entry',
      name: '',
      createdAt: '',
      updatedAt: '',
      entityId: '',
      price: null,
      currency: '',
      sku: '',
    },
  ];

  readonly messageOptions: IProductResponse[] = [
    {
      id: 'type-to-search',
      name: '',
      createdAt: '',
      updatedAt: '',
      entityId: '',
      price: null,
      currency: '',
      sku: '',
      disabled: true,
    },
    {
      id: 'not-found',
      name: '',
      createdAt: '',
      updatedAt: '',
      entityId: '',
      price: null,
      currency: '',
      sku: '',
      disabled: true,
    },
  ];

  searchInput$ = new Subject<string>();
  private isSelecting$ = new BehaviorSubject<boolean>(false);

  constructor(private mobileViewService: MobileViewService, private cdr: ChangeDetectorRef) {
    this.isDeviceMobile$ = this.mobileViewService.mobileDevice;
  }
  ngOnChanges(changes: SimpleChanges): void {
    if (changes.products || changes.loading) {
      this.updateProductList();
      if (this.selectedProductId) {
        this.productSelect.writeValue(this.selectedProductId);
      }
    }
  }

  ngOnInit(): void {
    this.searchInput$.pipe(debounceTime(1000)).subscribe((term) => {
      this.searchTerm.emit(term);
    });
  }

  onClear(): void {
    this.products = [];
    this.updatedProducts = [];
    this.loading = false;
    this.showLoadMore = false;
    this.notFoundMessage = CustomMessages.TypeToSearchMessage;
    this.updateProductList();
    this.resetProduct.emit();
  }

  onProductSelection(product: IProductResponse | null): void {
    if (product) {
      this.isSelecting$.next(true);
      if (product.id === 'add-to-catalog') {
        this.selectedProduct.emit(product);
        this.productSelect.clearModel();
      } else {
        this.selectedProduct.emit(product);
      }
      this.isSelecting$.next(false);
    } else {
      this.onClear();
    }
  }

  onSearch(term: string): void {
    if (!term || term.length < 2) {
      this.notFoundMessage = CustomMessages.TypeToSearchMessage;
      this.updatedProducts = [...this.extraOptions, this.messageOptions[0]];
      this.searchTerm.emit('');
      return;
    }
    this.loading = true;
    this.updatedProducts = [];
    this.notFoundMessage = CustomMessages.NotFoundMessage;
    this.searchInput$.next(term);
  }

  onLoadMore(): void {
    this.loadMore.emit();
  }

  compareProducts(p1: IProductResponse, p2: IProductResponse): boolean {
    return p1 && p2 ? p1.id === p2.id : p1 === p2;
  }

  private updateProductList(): void {
    if (this.loading) {
      this.updatedProducts = [];
      this.cdr.detectChanges();
      return;
    }
    if (this.products?.length) {
      this.updatedProducts = [...this.extraOptions, ...this.products];
    } else if (this.notFoundMessage === CustomMessages.NotFoundMessage) {
      this.updatedProducts = [...this.extraOptions, this.messageOptions[1]];
    } else {
      this.updatedProducts = [...this.extraOptions, this.messageOptions[0]];
    }
    this.cdr.detectChanges();
  }
}
